import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ShieldCheck, Plus, Calendar, AlertTriangle, Edit, Trash2, Loader2,
  Search, Paperclip, Download, FileText, Building, Filter, Eye,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import AttachmentPreviewModal from '../components/AttachmentPreviewModal';

const EMPTY_FORM = {
  office_id: '',
  name: '',
  credential_type: 'incorporation_certificate',
  issuing_authority: '',
  certificate_number: '',
  issue_date: '',
  expiry_date: '',
  notes: '',
};

const statusBadge = (item) => {
  if (item.is_expired) {
    return <Badge variant="destructive" data-testid="cred-status-expired">Expired</Badge>;
  }
  if (item.is_expiring_soon) {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid="cred-status-expiring">
        Expiring soon
      </Badge>
    );
  }
  if (item.expiry_date) {
    return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Valid</Badge>;
  }
  return <Badge variant="secondary">No expiry</Badge>;
};

const CompanyCredentials = () => {
  const { getAuthHeaders, currentOffice, offices, isSuperAdmin } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const isSA = isSuperAdmin();

  const [credentials, setCredentials] = useState([]);
  const [types, setTypes] = useState([]);
  const [allOffices, setAllOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [officeFilter, setOfficeFilter] = useState('all');
  const [showOnlyExpiring, setShowOnlyExpiring] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [formError, setFormError] = useState('');
  const [previewItem, setPreviewItem] = useState(null);

  const fetchTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/company-credentials/types`, { headers: getAuthHeaders() });
      if (res.ok) setTypes(await res.json());
    } catch (e) { console.error(e); }
  }, [API_URL, getAuthHeaders]);

  const fetchOffices = useCallback(async () => {
    if (!isSA) return;
    try {
      const res = await fetch(`${API_URL}/api/offices/`, { headers: getAuthHeaders() });
      if (res.ok) setAllOffices(await res.json());
    } catch (e) { console.error(e); }
  }, [API_URL, getAuthHeaders, isSA]);

  const fetchCredentials = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      if (search.trim()) params.append('search', search.trim());
      if (showOnlyExpiring) params.append('expiring_soon', 'true');
      if (isSA && officeFilter !== 'all') params.append('office_id', officeFilter);
      const res = await fetch(`${API_URL}/api/company-credentials/?${params.toString()}`, { headers });
      if (res.ok) {
        setCredentials(await res.json());
      } else {
        toast.error('Failed to load credentials');
      }
    } catch (e) {
      console.error(e);
      toast.error('Network error loading credentials');
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeaders, isSA, officeFilter, search, showOnlyExpiring]);

  useEffect(() => { fetchTypes(); fetchOffices(); }, [fetchTypes, fetchOffices]);
  useEffect(() => { fetchCredentials(); }, [fetchCredentials]);

  const resetForm = () => {
    setFormData({
      ...EMPTY_FORM,
      office_id: isSA ? '' : (currentOffice?.id || (offices?.[0]?.id) || ''),
    });
    setAttachmentFile(null);
    setEditing(null);
    setFormError('');
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (cred) => {
    setEditing(cred);
    setFormData({
      office_id: cred.office_id,
      name: cred.name || '',
      credential_type: cred.credential_type || 'other',
      issuing_authority: cred.issuing_authority || '',
      certificate_number: cred.certificate_number || '',
      issue_date: cred.issue_date || '',
      expiry_date: cred.expiry_date || '',
      notes: cred.notes || '',
    });
    setAttachmentFile(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.name.trim()) {
      setFormError('Please enter a credential name');
      toast.error('Please enter a credential name');
      return;
    }
    if (isSA && !editing && !formData.office_id) {
      setFormError('Please select an office before saving');
      toast.error('Please select an office');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        credential_type: formData.credential_type,
        issuing_authority: formData.issuing_authority || null,
        certificate_number: formData.certificate_number || null,
        issue_date: formData.issue_date || null,
        expiry_date: formData.expiry_date || null,
        notes: formData.notes || null,
      };
      if (!editing && isSA) {
        payload.office_id = formData.office_id;
      }
      if (attachmentFile) {
        const reader = new FileReader();
        const base64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(attachmentFile);
        });
        payload.file_base64 = base64;
        payload.file_name = attachmentFile.name;
        payload.file_mime = attachmentFile.type || 'application/octet-stream';
      }

      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const url = editing
        ? `${API_URL}/api/company-credentials/${editing.id}`
        : `${API_URL}/api/company-credentials/`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success(editing ? 'Credential updated' : 'Credential added');
        setShowModal(false);
        resetForm();
        fetchCredentials();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save credential');
      }
    } catch (e) {
      console.error(e);
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this credential? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/api/company-credentials/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        toast.success('Credential deleted');
        fetchCredentials();
      } else {
        toast.error('Failed to delete');
      }
    } catch (e) {
      toast.error('Network error');
    }
  };

  const handleDownload = async (cred) => {
    setDownloadingId(cred.id);
    try {
      const headers = getAuthHeaders();
      delete headers['Content-Type'];
      const res = await fetch(`${API_URL}/api/company-credentials/${cred.id}/file`, { headers });
      if (!res.ok) {
        toast.error('Failed to download');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = cred.file_name || `${cred.name}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e) {
      toast.error('Failed to download');
    } finally {
      setDownloadingId(null);
    }
  };

  const expiringCount = credentials.filter((c) => c.is_expiring_soon || c.is_expired).length;

  return (
    <div className="p-6 space-y-6" data-testid="company-credentials-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-blue-600" />
            Company Credentials
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isSA
              ? 'Statutory documents across all offices — incorporation certificates, business licenses, tax certifications and more.'
              : 'Statutory documents for this office. Upload your incorporation certificate, business license, TIN, VRN and more.'}
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-credential-btn" className="gap-2">
          <Plus className="h-4 w-4" /> Add Credential
        </Button>
      </div>

      {expiringCount > 0 && (
        <Card className="border-amber-300 bg-amber-50/60 dark:bg-amber-950/20">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
            <div className="text-sm">
              <span className="font-semibold text-amber-900 dark:text-amber-200">
                {expiringCount} credential{expiringCount > 1 ? 's' : ''}
              </span>{' '}
              <span className="text-amber-800 dark:text-amber-300">
                {expiringCount > 1 ? 'are' : 'is'} expired or expiring within 30 days.
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, issuing authority, certificate number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="cred-search-input"
            />
          </div>
          {isSA && (
            <Select value={officeFilter} onValueChange={setOfficeFilter}>
              <SelectTrigger className="w-[200px]" data-testid="cred-office-filter">
                <SelectValue placeholder="Filter by office" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All offices</SelectItem>
                {allOffices.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant={showOnlyExpiring ? 'default' : 'outline'}
            onClick={() => setShowOnlyExpiring((v) => !v)}
            className="gap-2"
            data-testid="cred-expiring-toggle"
          >
            <Filter className="h-4 w-4" />
            {showOnlyExpiring ? 'Showing expiring only' : 'Show expiring only'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading credentials...
            </div>
          ) : credentials.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <ShieldCheck className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No credentials yet</p>
              <p className="text-sm">Click "Add Credential" to upload your first document.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    {isSA && <th className="px-4 py-3 font-medium">Office</th>}
                    <th className="px-4 py-3 font-medium">Cert #</th>
                    <th className="px-4 py-3 font-medium">Issued</th>
                    <th className="px-4 py-3 font-medium">Expires</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {credentials.map((c) => (
                    <tr key={c.id} className="border-t hover:bg-muted/20 transition-colors" data-testid={`cred-row-${c.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{c.name}</div>
                        {c.issuing_authority && (
                          <div className="text-xs text-muted-foreground">{c.issuing_authority}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">{c.credential_type_label}</td>
                      {isSA && (
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-xs">
                            <Building className="h-3 w-3 text-muted-foreground" />
                            {c.office_name || '—'}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-3 font-mono text-xs">{c.certificate_number || '—'}</td>
                      <td className="px-4 py-3 text-xs">
                        {c.issue_date ? <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3 opacity-60" />{c.issue_date}</span> : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {c.expiry_date ? (
                          <div>
                            <div className="inline-flex items-center gap-1"><Calendar className="h-3 w-3 opacity-60" />{c.expiry_date}</div>
                            {typeof c.days_until_expiry === 'number' && (
                              <div className={`text-[11px] mt-0.5 ${c.is_expired ? 'text-red-600' : c.is_expiring_soon ? 'text-amber-600' : 'text-muted-foreground'}`}>
                                {c.is_expired ? `${Math.abs(c.days_until_expiry)} days ago` : `in ${c.days_until_expiry} days`}
                              </div>
                            )}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">{statusBadge(c)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {c.has_file && (
                            <>
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => setPreviewItem(c)}
                                data-testid={`cred-preview-${c.id}`}
                                title="Preview file"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => handleDownload(c)}
                                disabled={downloadingId === c.id}
                                data-testid={`cred-download-${c.id}`}
                                title="Download attached file"
                              >
                                {downloadingId === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`cred-edit-${c.id}`} title="Edit">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} data-testid={`cred-delete-${c.id}`} title="Delete" className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={(open) => { if (!open) { setShowModal(false); resetForm(); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle data-testid="cred-modal-title">
              {editing ? 'Edit Credential' : 'Add Company Credential'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <div
                className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2"
                data-testid="cred-form-error"
              >
                {formError}
              </div>
            )}
            {isSA && !editing && (
              <div className="space-y-2">
                <Label>Office *</Label>
                <Select
                  value={formData.office_id}
                  onValueChange={(v) => setFormData({ ...formData, office_id: v })}
                >
                  <SelectTrigger data-testid="cred-form-office">
                    <SelectValue placeholder="Select office" />
                  </SelectTrigger>
                  <SelectContent>
                    {allOffices.map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Certificate of Incorporation"
                  data-testid="cred-form-name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select
                  value={formData.credential_type}
                  onValueChange={(v) => setFormData({ ...formData, credential_type: v })}
                >
                  <SelectTrigger data-testid="cred-form-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Issuing Authority</Label>
                <Input
                  value={formData.issuing_authority}
                  onChange={(e) => setFormData({ ...formData, issuing_authority: e.target.value })}
                  placeholder="e.g. BRELA, TRA"
                />
              </div>
              <div className="space-y-2">
                <Label>Certificate Number</Label>
                <Input
                  value={formData.certificate_number}
                  onChange={(e) => setFormData({ ...formData, certificate_number: e.target.value })}
                  placeholder="e.g. 12345-678"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Issue Date</Label>
                <Input type="date" value={formData.issue_date}
                  onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input type="date" value={formData.expiry_date}
                  onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional information..."
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Paperclip className="h-4 w-4" /> Attach File (PDF or image)
              </Label>
              <Input
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                data-testid="cred-form-file"
              />
              {editing?.has_file && !attachmentFile && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  Existing file: {editing.file_name || 'attached'} (upload a new file to replace)
                </p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="cred-form-submit">
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editing ? 'Save Changes' : 'Add Credential'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AttachmentPreviewModal
        open={!!previewItem}
        onClose={() => setPreviewItem(null)}
        title={previewItem?.name || 'Credential'}
        downloadName={previewItem?.file_name || `${previewItem?.name || 'credential'}.pdf`}
        fetchUrl={previewItem ? `${API_URL}/api/company-credentials/${previewItem.id}/file` : ''}
        headers={getAuthHeaders()}
        mime={previewItem?.file_mime}
      />
    </div>
  );
};

export default CompanyCredentials;
